
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SEO } from './SEO';
import {
  Security as SecurityIcon,
  VerifiedUser as VerifiedUserIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  GppGood as GppGoodIcon,
  SystemSecurityUpdateGood as SystemSecurityUpdateGoodIcon,
  AccessAlarm as AccessAlarmIcon,
  PrivacyTip as PrivacyTipIcon,
} from '@mui/icons-material';
import LocalizedLink from './LocalizedLink';

const SarvalokSecurity: React.FC = () => {
  const { t } = useTranslation(['security', 'seo']);

  return (
    <div className="bg-white text-black px-8 lg:px-20 py-4">
      <SEO
        title={t('seo:security.title', { defaultValue: 'Security & Privacy | Sarvalok AI Platform' })}
        description={t('seo:security.description', { defaultValue: "Learn about Sarvalok's comprehensive security measures, data protection protocols, and privacy safeguards for your cultural research and spiritual studies." })}
        url="/security"
      />
      <h2 className="text-2xl font-semibold mb-6">{t('security.title')}</h2>

      <p className="mb-4">
        {t('security.introduction')}
      </p>

      <h3 className="text-xl font-semibold mb-4">{t('security.infrastructure.title')}</h3>
      <p className="mb-4">
        {t('security.infrastructure.description')}
      </p>
      <ul className="list-disc list-inside ml-6 mb-6">
        <li className="mb-2">
          <span className="font-semibold">{t('security.infrastructure.physicalSecurity.title')}</span> {t('security.infrastructure.physicalSecurity.description')}
        </li>
        <li className="mb-2">
          <span className="font-semibold">{t('security.infrastructure.operationalResilience.title')}</span> {t('security.infrastructure.operationalResilience.description')}
        </li>
        <li>
          <span className="font-semibold">{t('security.infrastructure.standardsAlignment.title')}</span> {t('security.infrastructure.standardsAlignment.description')}
        </li>
      </ul>

      <h3 className="text-xl font-semibold mb-4">{t('security.encryption.title')}</h3>
      <p className="mb-4">
        {t('security.encryption.description')}
      </p>
      <ul className="list-disc list-inside ml-6 mb-6">
        <li className="mb-2">
          <span className="font-semibold">{t('security.encryption.inTransit.title')}</span> {t('security.encryption.inTransit.description')}
        </li>
        <li>
          <span className="font-semibold">{t('security.encryption.atRest.title')}</span> {t('security.encryption.atRest.description')}
        </li>
      </ul>

      <h3 className="text-xl font-semibold mb-4">{t('security.network.title')}</h3>
      <p className="mb-4">
        {t('security.network.description')}
      </p>
      <ul className="list-disc list-inside ml-6 mb-6">
        <li className="mb-2">
          <span className="font-semibold">{t('security.network.firewalls.title')}</span> {t('security.network.firewalls.description')}
        </li>
        <li>
          <span className="font-semibold">{t('security.network.ddosMitigation.title')}</span> {t('security.network.ddosMitigation.description')}
        </li>
        <li>
          <span className="font-semibold">{t('security.network.intrusionDetection.title')}</span> {t('security.network.intrusionDetection.description')}
        </li>
      </ul>

      <h3 className="text-xl font-semibold mb-4">{t('security.application.title')}</h3>
      <p className="mb-4">
        <span className="font-semibold">{t('security.application.secureDevelopment.title')}</span> {t('security.application.secureDevelopment.description')}
      </p>
      <p className="mb-4">
        <span className="font-semibold">{t('security.application.aiSafety.title')}</span> {t('security.application.aiSafety.description')}
      </p>
      <p className="mb-4">
        <span className="font-semibold">{t('security.application.monitoring.title')}</span> {t('security.application.monitoring.description')}
      </p>

      <h3 className="text-xl font-semibold mb-4">{t('security.accessControl.title')}</h3>
      <p className="mb-4">
        {t('security.accessControl.description')}
      </p>
      <ul className="list-disc list-inside ml-6 mb-6">
        <li className="mb-2">
          <span className="font-semibold">{t('security.accessControl.leastPrivilege.title')}</span> {t('security.accessControl.leastPrivilege.description')}
        </li>
        <li>
          <span className="font-semibold">{t('security.accessControl.authentication.title')}</span> {t('security.accessControl.authentication.description')}
        </li>
      </ul>

      <h3 className="text-xl font-semibold mb-4">{t('security.enterprise.title')}</h3>
      <p className="mb-4">
        {t('security.enterprise.description')}
      </p>

      <h3 className="text-xl font-semibold mb-4">{t('security.userRole.title')}</h3>
      <p className="mb-4">
        {t('security.userRole.description')}
      </p>

      <h3 className="text-xl font-semibold mb-4">{t('security.privacy.title')}</h3>
      <p className="mb-4">
        {t('security.privacy.description')} <LocalizedLink to="/privacy-policy" className="text-blue-600 underline">{t('security.privacy.policyLink')}</LocalizedLink> {t('security.privacy.descriptionEnd')}
      </p>

      <h3 className="text-xl font-semibold mb-4">{t('security.improvement.title')}</h3>
      <p className="mb-4">
        {t('security.improvement.description')}
      </p>

      <h3 className="text-xl font-semibold mb-4">{t('security.reporting.title')}</h3>
      <p>
        {t('security.reporting.description')} <a href="mailto:security@sarvalok.com" className="text-blue-600 underline">security@sarvalok.com</a>. {t('security.reporting.descriptionEnd')}
      </p>
    </div>
  );
};

export default SarvalokSecurity;
